import type { ConversationRowDto, SkillDetailDto, SkillDraftDto, SkillPreviewDto, SkillSourceDto } from '@dbrain/shared';
import { Alert, Button, Dialog, EmptyState, IconButton, Icons, RadioCard, Skeleton, Tabs, TabPanel } from '@dbrain/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { capKeys } from '../../api/capabilities.js';
import { api, upload } from '../../api/client.js';
import { useDashboard, useSearch } from '../../api/hooks.js';
import { useExperience } from '../../experience.js';
import { ErrorAlert } from '../ErrorAlert.js';
import { useDebounced } from '../sessions/SessionSidebar.js';
import { useTimes } from '../status.js';
import { RepoNote, SkillChooser } from './SkillGithub.js';
import { formatSize, ScopeCards, SkillPreviewCard, targetBody, zipFolder, type Target, type TargetLevel } from './SkillParts.js';
import { SkillSourceError } from './SkillSourceError.js';

/**
 * "Skill ekle" (D-13, SA-054; docs/design/mockups/SkillEkle.dc.html,
 * SkillEkle2.dc.html): one dialog, four ways in. From a link (GitHub, a ZIP
 * or SKILL.md address, the organization's library) or from a file (a ZIP,
 * a folder, a SKILL.md, dropped or picked) a skill is always previewed
 * before it is installed; "Kendin yaz" opens the editor; "Bir sohbetten"
 * shows the steps and skills a conversation would give before the draft
 * opens in the editor. File contents, figures and personal data never go
 * into a skill drafted from a conversation.
 *
 * Phase 16c.3: a GitHub link is looked at first (POST /api/skills/source,
 * nothing downloaded): one skill opens its preview, with a note when the
 * repository itself is big (SkillGitHubBuyukDepo.dc.html); several open the
 * chooser, a skill over the limits locked (SkillGitHubCoklu.dc.html); only
 * the chosen folders are downloaded. A failure is a card with its reason in
 * the preview's place (SkillGitHubHatalar.dc.html, SkillGitHubCokBuyuk.dc.html).
 */
export type SkillAddTab = 'link' | 'file' | 'write' | 'conversation';
const TABS: SkillAddTab[] = ['link', 'file', 'write', 'conversation'];
const TAB_ICONS = { link: Icons.connections, file: Icons.upload, write: Icons.edit, conversation: Icons.chat } as const;

/** What the editor page is opened with (react-router state). */
export interface EditorState {
  draft?: SkillDraftDto;
  target?: Target;
}

function LinkTab({ source, setSource, onPreview, busy }: { source: string; setSource: (value: string) => void; onPreview: () => void; busy: boolean }) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <form
      className="skill-link"
      onSubmit={(e) => {
        e.preventDefault();
        if (source.trim()) onPreview();
      }}
    >
      <label htmlFor={id} className="ui-field__label">
        {t('skills.add.linkLabel')}
      </label>
      <div className="skill-link__row">
        <span className="skill-link__input">
          <Icons.connections size={16} />
          <input id={id} value={source} onChange={(e) => setSource(e.target.value)} placeholder={t('skills.add.placeholder')} autoFocus spellCheck={false} data-testid="skill-add-source" />
        </span>
        <Button type="submit" variant="secondary" busy={busy} disabled={!source.trim()} data-testid="skill-add-preview">
          {t('skills.add.preview')}
        </Button>
      </div>
      <p className="skill-link__examples">
        {t('skills.add.examples')}
        {['sahip/depo', 'https://…/SKILL.md', 'library:aylik-kapanis'].map((example) => (
          <code key={example}>{example}</code>
        ))}
      </p>
    </form>
  );
}

function FileTab({ file, onFiles, onFolder, onRemove, busy, done }: { file: { name: string; size: number } | null; onFiles: (files: File[]) => void; onFolder: (files: FileList) => void; onRemove: () => void; busy: boolean; done: boolean }) {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  return (
    <div className="skill-file">
      <div className="skill-drop">
        <span className="skill-drop__icon" aria-hidden="true">
          <Icons.upload size={21} />
        </span>
        <span className="skill-drop__title">{t('skills.add.dropTitle')}</span>
        <span className="skill-drop__buttons">
          <Button size="sm" variant="secondary" onClick={() => fileInput.current?.click()} disabled={busy} data-testid="skill-pick-file">
            {t('skills.add.pickFile')}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => folderInput.current?.click()} disabled={busy} data-testid="skill-pick-folder">
            {t('skills.add.pickFolder')}
          </Button>
        </span>
        <span className="skill-drop__hint">{t('skills.add.dropHint')}</span>
      </div>
      <input
        ref={fileInput}
        type="file"
        className="ui-visually-hidden"
        tabIndex={-1}
        aria-hidden="true"
        accept=".zip,.md,.toml"
        data-testid="skill-upload-file"
        onChange={(e) => {
          const picked = [...(e.target.files ?? [])];
          e.target.value = '';
          if (picked.length > 0) onFiles(picked);
        }}
      />
      <input
        ref={(node) => {
          folderInput.current = node;
          // a folder picker (not in the standard attribute list)
          node?.setAttribute('webkitdirectory', '');
        }}
        type="file"
        className="ui-visually-hidden"
        tabIndex={-1}
        aria-hidden="true"
        multiple
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) onFolder(e.target.files);
          e.target.value = '';
        }}
      />
      {file ? (
        <div className="skill-file__row" data-testid="skill-file-row">
          <span className="skill-file__badge" aria-hidden="true">
            {file.name.toLowerCase().endsWith('.zip') ? 'ZIP' : 'MD'}
          </span>
          <span className="skill-file__text">
            <span className="skill-file__name">{file.name}</span>
            <span className="skill-file__size">{formatSize(file.size)}</span>
          </span>
          {busy ? <span className="ui-spinner ui-spinner--inline" aria-hidden="true" /> : null}
          {done ? (
            <span className="skill-file__read">
              <Icons.done size={15} />
              {t('skills.add.read')}
            </span>
          ) : null}
          <IconButton size="sm" label={t('skills.add.removeFile')} icon={<Icons.close size={15} />} onClick={onRemove} />
        </div>
      ) : null}
    </div>
  );
}

function ConversationTab({ picked, onPick, draft, drafting, error }: { picked: string | null; onPick: (row: ConversationRowDto) => void; draft: SkillDraftDto | null; drafting: boolean; error: unknown }) {
  const { t } = useTranslation();
  const experience = useExperience();
  const dashboard = useDashboard();
  const times = useTimes();
  const [query, setQuery] = useState('');
  const q = useDebounced(query.trim(), 250);
  const search = useSearch(q, experience);
  const recent = dashboard.data?.recentConversations ?? [];
  const found = q.length >= 2 ? (search.data ?? []).map((hit) => ({ ...hit.conversation, summary: recent.find((r) => r.id === hit.conversation.id)?.summary ?? null })) : recent;
  return (
    <div className="skill-conversation">
      <label className="search-pill">
        <Icons.search size={16} />
        <span className="ui-visually-hidden">{t('skills.fromConversation.search')}</span>
        <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('skills.fromConversation.search')} data-testid="skill-conversation-search" />
      </label>
      <fieldset className="radio-list">
        <legend className="ui-field__label">{t('skills.fromConversation.which')}</legend>
        {dashboard.isPending ? <Skeleton lines={3} /> : null}
        {dashboard.data && found.length === 0 ? <p className="muted">{q.length >= 2 ? t('sessions.noResults') : t('dashboard.recent.empty')}</p> : null}
        {found.slice(0, 6).map((row) => (
          <RadioCard
            key={row.id}
            name="skill-conversation"
            checked={picked === row.id}
            onChange={() => onPick(row as ConversationRowDto)}
            title={
              <span className="radio-row">
                <span>{row.title ?? t('conversation.untitled')}</span>
                <span className="radio-row__time">{times.list(row.lastActivityAt)}</span>
              </span>
            }
            hint={[row.workspaceName, row.summary ? t('skills.fromConversation.messages', { count: row.summary.messages }) : null].filter(Boolean).join(' · ')}
            data-testid="skill-conversation"
          />
        ))}
      </fieldset>
      {picked ? (
        <section className="skill-steps" aria-labelledby="skill-steps-title" aria-busy={drafting} data-testid="skill-steps">
          <h3 id="skill-steps-title">{t('skills.fromConversation.taken')}</h3>
          {drafting ? (
            <p className="skill-steps__wait">
              <span className="ui-spinner ui-spinner--inline" aria-hidden="true" />
              {t('skills.fromConversation.reading')}
            </p>
          ) : null}
          <ErrorAlert error={error} />
          {draft ? (
            <>
              {draft.steps.length > 0 ? (
                <ol>
                  {draft.steps.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
              ) : (
                <p className="muted">{t('skills.fromConversation.noSteps')}</p>
              )}
              <p className="skill-steps__skills" data-testid="skill-steps-skills">
                {draft.skills.length > 0 ? t('skills.fromConversation.skills', { names: draft.skills.join(', ') }) : t('skills.fromConversation.noSkills')}
              </p>
            </>
          ) : null}
          <p className="skill-steps__privacy">
            <Icons.shield size={15} />
            {t('skills.fromConversation.privacy')}
          </p>
        </section>
      ) : null}
    </div>
  );
}

export function SkillAddDialog({
  onClose,
  initialSource = '',
  initialTab,
  workspaceId,
  initialScope,
  conversationId,
}: {
  onClose: () => void;
  initialSource?: string;
  initialTab?: SkillAddTab;
  workspaceId?: string | undefined;
  /** "Skill'ler" of a workspace adds to that workspace */
  initialScope?: TargetLevel;
  /** "Save as a skill" of a conversation starts from it */
  conversationId?: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const titleId = useId();
  const [tab, setTab] = useState<SkillAddTab>(initialTab ?? (conversationId ? 'conversation' : 'link'));
  const [source, setSource] = useState(initialSource);
  // the link tab: a repository's skills to choose from (16c.3), then one preview per chosen skill
  const [chooser, setChooser] = useState<{ source: string; data: SkillSourceDto } | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [linkPreviews, setLinkPreviews] = useState<SkillPreviewDto[]>([]);
  const [filePreview, setFilePreview] = useState<SkillPreviewDto | null>(null);
  const [file, setFile] = useState<{ name: string; size: number; blob: Blob } | null>(null);
  const [target, setTarget] = useState<Target>(initialScope === 'workspace' && workspaceId ? { level: 'workspace', workspaceId } : { level: initialScope && initialScope !== 'workspace' ? initialScope : 'user' });
  const [done, setDone] = useState<SkillDetailDto[] | null>(null);
  const [picked, setPicked] = useState<string | null>(conversationId ?? null);
  const [drafts, setDrafts] = useState<Record<string, SkillDraftDto>>({});
  const [dragging, setDragging] = useState(false);
  const previewOf = (input: { source: string; folder?: string }) => api<SkillPreviewDto>('POST', '/api/skills/preview', input);
  // "Önizle": what the source holds first (nothing is downloaded); several skills open the chooser, one its preview
  const look = useMutation({
    mutationFn: async (input: { source: string; folder?: string }) => {
      if (input.folder !== undefined) return { chooser: null, previews: [await previewOf(input)] };
      const data = await api<SkillSourceDto>('POST', '/api/skills/source', { source: input.source });
      if (data.choices.length > 1) return { chooser: { source: input.source, data }, previews: [] };
      return { chooser: null, previews: [await previewOf({ source: input.source, ...(data.choices[0] ? { folder: data.choices[0].folder } : {}) })] };
    },
    onMutate: () => {
      setLinkPreviews([]);
      setChooser(null);
      setSelected(new Set());
    },
    onSuccess: (result) => {
      setChooser(result.chooser);
      setLinkPreviews(result.previews);
    },
  });
  // "2 skill'i önizle": only the chosen folders are downloaded, one after the other
  const lookMany = useMutation({
    mutationFn: async (input: { source: string; folders: string[] }) => {
      const out: SkillPreviewDto[] = [];
      for (const folder of input.folders) out.push(await previewOf({ source: input.source, folder }));
      return out;
    },
    onSuccess: (previews) => setLinkPreviews(previews),
  });
  const send = useMutation({
    mutationFn: (input: { blob: Blob; name: string; folder?: string }) =>
      upload<SkillPreviewDto>('POST', '/api/skills/upload', input.blob, {
        'x-file-name': encodeURIComponent(input.name),
        'content-type': 'application/octet-stream',
        ...(input.folder ? { 'x-skill-folder': input.folder } : {}),
      }),
    onSuccess: (data) => setFilePreview(data),
  });
  const install = useMutation({
    mutationFn: async (items: SkillPreviewDto[]) => {
      const installed: SkillDetailDto[] = [];
      for (const data of items) {
        installed.push(
          await api<SkillDetailDto>('POST', '/api/skills/install', {
            previewId: data.previewId,
            ...(data.existing && data.existing.level === target.level ? { skillId: data.existing.key } : targetBody(target)),
          }),
        );
      }
      return installed;
    },
    onSuccess: (skills) => setDone(skills),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: capKeys.skillsAll }),
  });
  const draft = useMutation({
    mutationFn: (id: string) => api<SkillDraftDto>('POST', `/api/conversations/${id}/skill-draft`, {}),
    onSuccess: (data, id) => setDrafts((current) => ({ ...current, [id]: data })),
  });
  // `/skill add <source>` and a conversation's "save as a skill" start at once
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (initialSource.trim()) look.mutate({ source: initialSource.trim() });
    if (conversationId) draft.mutate(conversationId);
  }, [initialSource, conversationId, look, draft]);

  const takeFiles = (files: File[]) => {
    const first = files[0];
    if (!first) return;
    setTab('file');
    setFilePreview(null);
    if (files.length === 1 && /\.(zip|md|toml)$/i.test(first.name)) {
      setFile({ name: first.name, size: first.size, blob: first });
      send.mutate({ blob: first, name: first.name });
      return;
    }
    // several loose files: one folder
    void zipFolder(files).then((blob) => {
      setFile({ name: 'folder.zip', size: blob.size, blob });
      send.mutate({ blob, name: 'folder.zip' });
    });
  };
  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const files = [...event.dataTransfer.files];
    if (files.length > 0) takeFiles(files);
  };
  const previewLink = () => {
    // a new look forgets the chosen skills' failed preview
    lookMany.reset();
    install.reset();
    look.mutate({ source: source.trim() });
  };
  const previewChosen = () => chooser && lookMany.mutate({ source: chooser.source, folders: chooser.data.choices.filter((c) => !c.over && selected.has(c.folder)).map((c) => c.folder) });
  const shown = tab === 'link' ? linkPreviews : tab === 'file' && filePreview ? [filePreview] : [];
  const choosing = tab === 'link' && chooser !== null && linkPreviews.length === 0;
  const linkError = look.error ?? lookMany.error;
  const pickedDraft = picked ? (drafts[picked] ?? null) : null;
  const subtitle = tab === 'conversation' ? t('skills.add.subtitleConversation') : t('skills.add.subtitle');
  const single = shown.length === 1 ? shown[0] : undefined;

  const footer = done ? (
    <>
      <Button variant="ghost" onClick={onClose} data-testid="skill-add-close">
        {t('common.close')}
      </Button>
      <Button
        onClick={() => {
          onClose();
          void navigate(`/skills?open=${encodeURIComponent(done[0]?.key ?? '')}`);
        }}
        data-testid="skill-add-open"
      >
        {t('skills.add.open')}
      </Button>
    </>
  ) : tab === 'write' ? (
    <>
      <Button variant="ghost" onClick={onClose}>
        {t('common.cancel')}
      </Button>
      <Button
        onClick={() => {
          onClose();
          void navigate('/skills/new', { state: { target } satisfies EditorState });
        }}
        data-testid="skill-write-start"
      >
        {t('skills.add.writeStart')}
      </Button>
    </>
  ) : tab === 'conversation' ? (
    <>
      <span className="skill-dialog__hint">{t('skills.fromConversation.footer')}</span>
      <Button variant="ghost" onClick={onClose}>
        {t('common.cancel')}
      </Button>
      <Button
        disabled={!pickedDraft}
        onClick={() => {
          if (!pickedDraft) return;
          onClose();
          void navigate('/skills/new', { state: { draft: pickedDraft, target } satisfies EditorState });
        }}
        data-testid="skill-draft-create"
      >
        {t('skills.fromConversation.create')}
      </Button>
    </>
  ) : (
    <>
      <span className="skill-dialog__hint skill-dialog__hint--drag">
        <Icons.upload size={15} />
        {t('skills.add.dragHint')}
      </span>
      <Button variant="ghost" onClick={onClose}>
        {t('common.cancel')}
      </Button>
      {choosing ? (
        <Button disabled={selected.size === 0} busy={lookMany.isPending} onClick={previewChosen} data-testid="skill-preview-chosen">
          {t('skills.github.previewChosen', { count: selected.size })}
        </Button>
      ) : (
        <Button disabled={shown.length === 0 || shown.some((p) => p.candidates.length > 0)} busy={install.isPending} onClick={() => install.mutate(shown)} data-testid="skill-install">
          {shown.length > 1 ? t('skills.github.installAll', { count: shown.length }) : single?.existing && single.existing.level === target.level ? t('skills.add.update') : t('skills.add.install')}
        </Button>
      )}
    </>
  );

  return (
    <Dialog title={t('skills.add.title')} closeLabel={t('common.close')} onClose={onClose} size="wide" bare labelledBy={titleId} className="skill-dialog" data-testid="skill-add-dialog">
      <div
        className={dragging ? 'skill-dialog__frame skill-dialog__frame--drop' : 'skill-dialog__frame'}
        onDragOver={(e) => {
          if (done) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(e) => {
          if (e.currentTarget === e.target) setDragging(false);
        }}
        onDrop={onDrop}
      >
        <header className="skill-dialog__header">
          <div>
            <h2 id={titleId} className="skill-dialog__title">
              {t('skills.add.title')}
            </h2>
            <p className="skill-dialog__subtitle">{subtitle}</p>
          </div>
          <IconButton label={t('common.close')} icon={<Icons.close />} onClick={onClose} data-testid="dialog-close" />
        </header>
        <div className="skill-dialog__body">
          {done ? (
            done.length === 1 ? (
              <Alert tone="success" title={t('skills.add.installed', { name: done[0]?.name })} data-testid="skill-installed">
                {t('skills.add.installedHint', { name: done[0]?.name })}
              </Alert>
            ) : (
              <Alert tone="success" title={t('skills.github.installedMany', { names: done.map((d) => d.name).join(', ') })} data-testid="skill-installed">
                {t('skills.github.installedManyHint')}
              </Alert>
            )
          ) : (
            <>
              <Tabs
                tabsId="skill-add"
                variant="segmented"
                className="ui-tabs--fill"
                label={t('skills.add.sources')}
                value={tab}
                onChange={setTab}
                tabs={TABS.map((id) => {
                  const Icon = TAB_ICONS[id];
                  return { id, label: t(`skills.add.tabs.${id}`), icon: <Icon size={15} />, 'data-testid': `skill-add-tab-${id}` };
                })}
              />
              <TabPanel tabsId="skill-add" id={tab} className="skill-dialog__panel">
                {tab === 'link' ? <LinkTab source={source} setSource={setSource} onPreview={previewLink} busy={look.isPending} /> : null}
                {tab === 'file' ? (
                  <FileTab
                    file={file}
                    busy={send.isPending}
                    done={filePreview !== null}
                    onFiles={takeFiles}
                    onFolder={(files) => takeFiles([...files])}
                    onRemove={() => {
                      setFile(null);
                      setFilePreview(null);
                      send.reset();
                    }}
                  />
                ) : null}
                {tab === 'write' ? (
                  <EmptyState icon={<Icons.edit />} title={t('skills.add.writeTitle')} data-testid="skill-write">
                    {t('skills.add.writeHint')}
                  </EmptyState>
                ) : null}
                {tab === 'conversation' ? (
                  <ConversationTab
                    picked={picked}
                    onPick={(row) => {
                      setPicked(row.id);
                      if (!drafts[row.id] && !(draft.isPending && draft.variables === row.id)) draft.mutate(row.id);
                    }}
                    draft={pickedDraft}
                    drafting={draft.isPending && draft.variables === picked}
                    error={draft.variables === picked ? draft.error : null}
                  />
                ) : null}
                {tab === 'link' ? (
                  <SkillSourceError
                    error={linkError}
                    onUpload={() => setTab('file')}
                    onRetry={() => (lookMany.error ? previewChosen() : look.variables ? look.mutate(look.variables) : previewLink())}
                    retrying={look.isPending || lookMany.isPending}
                  />
                ) : null}
                {tab === 'file' ? <ErrorAlert error={send.error} /> : null}
                {choosing && chooser ? (
                  <SkillChooser
                    source={chooser.data}
                    selected={selected}
                    onToggle={(folder) =>
                      setSelected((current) => {
                        const next = new Set(current);
                        if (next.has(folder)) next.delete(folder);
                        else next.add(folder);
                        return next;
                      })
                    }
                  />
                ) : null}
                {shown.length > 0 && (tab === 'link' || tab === 'file') ? (
                  <>
                    {single?.repository?.large && !chooser ? (
                      <RepoNote repository={single.repository} name={single.name} files={single.files.length} bytes={single.files.reduce((sum, f) => sum + f.size, 0)} />
                    ) : null}
                    {shown.map((item) => (
                      <div key={item.previewId} className="skill-dialog__preview">
                        <SkillPreviewCard
                          preview={item}
                          onChooseFolder={(folder) => (tab === 'link' ? look.mutate({ source: source.trim(), folder }) : file && send.mutate({ blob: file.blob, name: file.name, folder }))}
                        />
                        {item.existing ? <Alert tone="info" title={t('skills.add.existing', { level: t(`skills.levels.${item.existing.level}`), version: item.existing.version ?? '—' })} /> : null}
                      </div>
                    ))}
                    <ErrorAlert error={install.error} />
                  </>
                ) : null}
              </TabPanel>
            </>
          )}
        </div>
        {/* "Kimler kullanabilir?" on every tab, in the fixed bottom part above Vazgeç and Kur: in sight however long the
            preview is (a menu here would open past the dialog's edge) */}
        {done ? null : (
          <div className="skill-dialog__scope">
            <ScopeCards value={target} onChange={setTarget} workspaceId={workspaceId} />
          </div>
        )}
        <footer className="skill-dialog__footer">{footer}</footer>
      </div>
    </Dialog>
  );
}
